import { useState, useCallback, useEffect, useRef } from 'react'
import { Search, X, Shield, FileText } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import type { MonitorEventItem, MonitorEntityItem, MonitorSearchResponse } from '@/lib/api'

interface MonitorSearchProps {
  open: boolean
  onClose: () => void
  onEventClick: (event: MonitorEventItem) => void
  onCountryClick: (countryCode: string) => void
}

const DEBOUNCE_MS = 280
const MIN_QUERY_LENGTH = 2

export default function MonitorSearch({
  open,
  onClose,
  onEventClick,
  onCountryClick,
}: MonitorSearchProps) {
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!open) return
    inputRef.current?.focus()
    setQuery('')
    setDebouncedQuery('')
  }, [open])

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [query])

  const { data, isLoading, isFetching } = useQuery<MonitorSearchResponse>({
    queryKey: ['monitor-search', debouncedQuery],
    queryFn: () => apiClient.getMonitorSearch(debouncedQuery, 12, 8),
    enabled: open && debouncedQuery.length >= MIN_QUERY_LENGTH,
    staleTime: 60_000,
    retry: 1,
  })

  const handleEventSelect = useCallback(
    (ev: MonitorEventItem) => {
      onEventClick(ev)
      onClose()
    },
    [onEventClick, onClose]
  )

  const handleEntitySelect = useCallback(
    (entity: MonitorEntityItem) => {
      if (entity.country_code) onCountryClick(entity.country_code)
      onClose()
    },
    [onCountryClick, onClose]
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    },
    [onClose]
  )

  if (!open) return null

  const hasQuery = debouncedQuery.length >= MIN_QUERY_LENGTH
  const events = data?.events ?? []
  const entities = data?.entities ?? []
  const empty = hasQuery && !isLoading && !isFetching && events.length === 0 && entities.length === 0

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[4.5rem] px-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div
        className="w-full max-w-xl bg-gray-950 border border-green-900/50 rounded-lg shadow-xl shadow-green-950/30 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-3 py-2 border-b border-green-900/40">
          <Search className="w-4 h-4 text-green-400 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search events, actors, CVE, country…"
            className="flex-1 bg-transparent text-sm text-gray-200 placeholder-gray-600 font-mono outline-none"
            autoComplete="off"
          />
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded text-gray-500 hover:text-gray-300 hover:bg-gray-800/50 transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {!hasQuery && (
            <div className="px-4 py-6 text-center text-[11px] text-gray-600 font-mono">
              Type at least {MIN_QUERY_LENGTH} characters to search events and entities
            </div>
          )}
          {hasQuery && (isLoading || isFetching) && (
            <div className="px-4 py-8 text-center text-[11px] text-green-400/80 font-mono">Searching…</div>
          )}
          {empty && (
            <div className="px-4 py-8 text-center text-[11px] text-gray-500 font-mono">No events or entities found</div>
          )}
          {hasQuery && data && !isLoading && !isFetching && (events.length > 0 || entities.length > 0) && (
            <>
              {entities.length > 0 && (
                <div className="px-2 py-1.5 border-b border-gray-800/50">
                  <div className="flex items-center gap-1.5 text-[9px] text-gray-500 uppercase tracking-wider font-mono mb-1">
                    <Shield className="w-3 h-3" /> Entities ({data.total_entities})
                  </div>
                  <ul className="space-y-0.5">
                    {entities.map((ent) => (
                      <li key={ent.id}>
                        <button
                          type="button"
                          onClick={() => handleEntitySelect(ent)}
                          className="w-full text-left px-2 py-1.5 rounded text-[11px] font-mono text-gray-300 hover:bg-green-500/10 hover:text-green-300 transition-colors flex items-center justify-between gap-2"
                        >
                          <span className="truncate">{ent.name}</span>
                          <span className="text-[9px] text-gray-600 flex-shrink-0">
                            {ent.entity_type === 'threat_actor' ? 'Actor' : 'Ransomware'} · {ent.event_count} events
                            {ent.country_code && ` · ${ent.country_code}`}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {events.length > 0 && (
                <div className="px-2 py-1.5">
                  <div className="flex items-center gap-1.5 text-[9px] text-gray-500 uppercase tracking-wider font-mono mb-1">
                    <FileText className="w-3 h-3" /> Events ({data.total_events})
                  </div>
                  <ul className="space-y-0.5">
                    {events.map((ev) => (
                      <li key={ev.id}>
                        <button
                          type="button"
                          onClick={() => handleEventSelect(ev)}
                          className="w-full text-left px-2 py-1.5 rounded text-[11px] font-mono text-gray-300 hover:bg-green-500/10 hover:text-green-300 transition-colors"
                        >
                          <span className="line-clamp-2">{ev.title}</span>
                          <span className="text-[9px] text-gray-600 mt-0.5 block">
                            {ev.severity} · {ev.event_type} {ev.country_code && `· ${ev.country_code}`}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
